import Head from "next/head";
import Link from "next/link";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { constants } from "src/shared/constants";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

const PLATFORM_TERMS_URL = "https://www.attend.tech/terms-of-use";
const PLATFORM_PRIVACY_URL = "https://www.attend.tech/privacy-policy";
const TEAM_TERMS_URL = "https://www.coloradorapids.com/terms-of-use";
const TEAM_PRIVACY_URL = "https://www.coloradorapids.com/privacy-policy";
const TEAM_TICKET_TERMS_URL =
  "https://www.coloradorapids.com/tickets/terms-conditions";
const TEAM_TICKET_HOLDER_AGREEMENT_URL = "https://ballarena.events/ksetixback";
const TEAM_APP_URL = "https://www.coloradorapids.com/app";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "What is the Pids Pass?",
    answer:
      "The pass is a yearly membership that gets you access to all regular season Colorado Rapids home matches. Tickets will be assigned prior to kickoff and can be found in the Colorado Rapids App.",
  },
  {
    question: "What matches are included in the Pids Pass?",
    answer:
      "Your Pids Pass includes access to all 24 Colorado Rapids regular season home matches, regardless of opponent, across the 2027 Sprint Season (7 matches) and 2027–28 MLS Regular Season (17 matches). The 2027 Sprint Season schedule is available now, with the 2027–28 MLS Regular Season schedule expected to be announced in March or April 2027.",
  },
  {
    question: "Is a seat guaranteed for every match?",
    answer: "Seats are guaranteed for every match mentioned above.",
  },
  {
    question: "How do I get my tickets?",
    answer:
      "Tickets will be assigned 4 hours prior to match time. You will receive a text once tickets are in your account.",
  },
  {
    question: "Where will my seat be located?",
    answer:
      "Your seat location will be assigned each match based on availability.",
  },
  {
    question: "Can I add additional tickets for guests?",
    answer:
      "Yes! Additional guest tickets are available for purchase, and need to be purchased no later than 4 hours prior to the match.",
  },
  {
    question: "How do I upgrade my seats?",
    answer:
      'You\'ll have the opportunity to purchase upgraded tickets. Simply select the "Add-Ons" option below your game to see all available upgrades, subject to availability.',
  },
  {
    question: "When are my tickets delivered?",
    answer: (
      <>
        Tickets will be delivered to the{" "}
        <Link target="_blank" href={TEAM_APP_URL}>
          Colorado Rapids App
        </Link>{" "}
        4 hours prior to the match.
      </>
    ),
  },
  {
    question: "How do I enter the venue?",
    answer: "Present your barcoded mobile tickets at the gates.",
  },
  {
    question: "Can I purchase multiple Passes?",
    answer:
      "Yes! For example, a family of 4 should have one person purchase 4 Passes, then 4 seats will be delivered together to that person's account manager.",
  },
  {
    question: "Can I bring different people with me to each game?",
    answer:
      "Yes! Different people can attend each game. Simply transfer your Pass tickets to the people attending.",
  },
  {
    question:
      "Can I request to be seated with friends who are also Pids Pass holders?",
    answer:
      "Yes! You can create a group and invite friends to sit together in the Pass menu. All Pids Pass holders in the group must accept the invitation at least 4 hours prior to the match to be seated together.",
  },
  {
    question: "Can Pass tickets be transferred?",
    answer:
      "Yes! Pass tickets can be transferred to friends and family once they are in your account.",
  },
  {
    question: "What if I need ADA seating?",
    answer: (
      <>
        ADA accommodations can be requested by{" "}
        <Link href="mailto:rapidstickets@coloradorapids.com">
          rapidstickets@coloradorapids.com
        </Link>{" "}
        at least 4 hours prior to the match with the specific number of
        wheelchair spaces, companion seats, and/or semi-ambulatory seats
        required. Alternatively, ticketed guests may stop by our Guest Services
        window once inside the stadium for exchange, based on availability.
      </>
    ),
  },
  {
    question: "What happens if a game time changes?",
    answer:
      "All game times are subject to change. Your Pass ticket will be valid only for the date of the game and is non-exchangeable.",
  },
  {
    question: "What if one of my games is rained out or postponed?",
    answer:
      "If a game is postponed your Pass ticket will be valid for the rescheduled game only.",
  },
  {
    question:
      "Does the Pids Pass include U.S. Open Cup, Leagues Cup, and Playoff access?",
    answer:
      "The Pids Pass includes all 24 regular season home matches across the 2027 Sprint Season and 2027–28 MLS Regular Season. Pids Pass holders will receive presale access and discounted tickets for all Colorado Rapids home U.S. Open Cup, Leagues Cup, and Playoff matches.",
  },
];

// New User Registration terms (auth page opt-in)
export const REGISTRATION_TERMS_AND_CONDITION = () => (
  <>
    By creating an account, you agree to receive marketing and promotional
    emails from Colorado Rapids at the email address provided which may include
    ticket offers, event information, promotions and other marketing
    communications. These communications may be sent by Colorado Rapids and/or
    its authorized service providers acting on its behalf, including Attend,
    using automated technologies. Message frequency may vary. Consent is not a
    condition of purchase. I understand that I may unsubscribe from marketing
    emails at any time by following the unsubscribe instructions included in the
    email communications. By checking this box, I acknowledge that I have read,
    understand, and agree to the Colorado Rapids{" "}
    <Link target="_blank" href={TEAM_TERMS_URL}>
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link target="_blank" href={TEAM_PRIVACY_URL}>
      Privacy Policy
    </Link>
    , as well as the Attend{" "}
    <Link target="_blank" href={PLATFORM_TERMS_URL}>
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link target="_blank" href={PLATFORM_PRIVACY_URL}>
      Privacy Policy
    </Link>
    , and I consent to the collection, use, and processing of my personal
    information in accordance with those policies.
  </>
);

// Checkout Purchase terms
export const TERMS_AND_CONDITION = () => (
  <>
    By purchasing, I understand and agree to the{" "}
    <Link target="_blank" href={TEAM_TICKET_TERMS_URL}>
      Ticket Purchase and Use Terms and Conditions
    </Link>{" "}
    and{" "}
    <Link target="_blank" href={TEAM_TICKET_HOLDER_AGREEMENT_URL}>
      Event Ticket Holder Agreement
    </Link>{" "}
    on behalf of myself and my minor child(ren) in attendance. I acknowledge
    that I have read, understand, and agree to Colorado Rapids{" "}
    <Link target="_blank" href={TEAM_TERMS_URL}>
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link target="_blank" href={TEAM_PRIVACY_URL}>
      Privacy Policy
    </Link>
    , as well as the Attend{" "}
    <Link target="_blank" href={PLATFORM_TERMS_URL}>
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link target="_blank" href={PLATFORM_PRIVACY_URL}>
      Privacy Policy
    </Link>
    , and I consent to the collection, use, and processing of my personal
    information in accordance with those policies.
  </>
);

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '892994483736752');
            fbq('track', 'PageView');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function (e) {
              try {
                const { cartRes, checkoutRes, passes } = e.detail;

                let content_name = "";
                if (cartRes.products && cartRes.products.length) {
                  content_name = "${TEAM_SCHEMA}_single_game_purchases";
                };

                if (cartRes.passes && cartRes.passes.length && passes.length) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_name = passDetail.passTitle;
                };

                fbq('track', 'InitiateCheckout', {
                  content_name: content_name, 
                  value: String(cartRes.totalAmount), 
                  currency: 'USD', 
                  order_id: checkoutRes.orderNumber,
                });

                console.log('meta pixel purchase event fired.');
              } catch(error) {
                console.log('meta pixel purchase event error:', error); 
              }
            });`,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=892994483736752&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};
